import { Button, ButtonSize, EditIcon, TextField } from "@bale/ui";
import { strings } from "intl/fa";
import React, { useCallback, useEffect, useState } from "react";
import { useInputHandler } from "hooks/useInputHandler";
import commonStyles from "./../Login.module.scss"; //styles that are common in initial pages (phone,otp,register)
import styles from "./OtpEntrance.module.scss";
import phoneEntranceStyles from "./../phoneEntrance/PhoneEntrance.module.scss"; //styles that are common in initial pages (phone,otp,register)
import { useTheme } from "@emotion/react";
import { digitsToHindi } from "@bale/utils";
import classNames from "classnames";
import { useRequest } from "../../../../hooks/useRequest";
import { AuthActions, AuthState } from "../../../../core/auth/auth.slice";
import { useAppDispatch, useAppSelector } from "../../../../utils/store";
import { GetTokenErrors } from "../../../../core/auth/auth.error";
import Footer from "../../../commons/footer/Footer";
import {
  getTokenThunk,
  requestOtpThunk,
} from "../../../../core/auth/auth.thunk";
import { toast } from "react-toastify";
import { sendYandexGoal, YandexGoal } from "../../../../yandex/yandex";
import { formatStrings } from "utils/utils";
import Modal from "components/commons/modal/Modal";
import HintModal from "../hintModal/HintModal";

const EXPIRATION_TIME = 120;
const OTP_LENGTH = 5;
type Props = {
  withSms?: boolean;
};
const OtpEntrance: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { sendRequest, loading } = useRequest(getTokenThunk);
  const { sendRequest: requestOTP } = useRequest(requestOtpThunk);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const phone = useAppSelector((state) => state.auth.phone);
  const otp = useInputHandler({
    numberOnly: true,
  });
  const [expireTimer, setExpireTimer] = useState(EXPIRATION_TIME);
  const [showLoginGuideModal, setShowLoginGuideModal] = useState(false);

  const closeLoginModal = useCallback(() => {
    setShowLoginGuideModal(false);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setExpireTimer((prevState) => prevState - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = useCallback(() => {
    sendRequest({ otp: otp.rawValue, noBaleAccount: props.withSms }).catch(
      (error: GetTokenErrors) => {
        otp.setError(strings.login.otp.error);
        toast.info(strings.login.otp.error, {
          toastId: strings.login.otp.error,
        });
      }
    );
    if (props.withSms) {
      sendYandexGoal(YandexGoal.EnterSmsBtn);
    } else {
      sendYandexGoal(YandexGoal.OtpEntranceBtn);
    }
  }, [otp.rawValue, props.withSms]);

  useEffect(() => {
    if (otp.rawValue.length === 5) {
      handleSubmit();
    }
  }, [otp.rawValue]);

  const onEditPhone = useCallback(() => {
    dispatch(AuthActions.setAuthState(AuthState.PhoneEntrance));
  }, []);

  const resendOTP = useCallback(() => {
    requestOTP({ phone });
    setExpireTimer(EXPIRATION_TIME);
  }, []);

  return (
    <>
      <div className={commonStyles.wrapper}>
        <div>
          <div className={styles.EditPhoneBox}>
            <div className={styles.Mobile}>
              <div className={styles.Label}>{strings.login.phone.label}</div>
              <div className={styles.Phone}>{digitsToHindi(phone)}</div>
            </div>
            <EditIcon
              size={24}
              color={theme.colors.dark[300]}
              cursor="pointer"
              onClick={onEditPhone}
            />
          </div>
          <p className={styles.OTPDesc}>
            {props.withSms ? (
              strings.login.sms_otp
            ) : (
              <>
                {strings.login.bale_otp[0]}
                <a
                  href="https://ble.ir/advertisement_assistant_bot"
                  target="_blank"
                  rel="noreferrer"
                  className={commonStyles.Link}
                >
                  {strings.login.bale_otp[1]}
                </a>
                {strings.login.bale_otp[2]}
                <p>{strings.login.bale_otp[3]}</p>
              </>
            )}
          </p>
        </div>
        <TextField
          pattern="^[0-9۰-۹٠-٩]*$"
          inputMode="numeric"
          label={strings.login.otp.label}
          placeholder={strings.login.otp.placeholder}
          autoFocus
          maxLength={OTP_LENGTH}
          {...otp}
        />
      </div>
      <div className={styles.otpHint}>
        {expireTimer <= 0 ? (
          <>
            <div className={phoneEntranceStyles.LoginProblem}>
              {strings.login.login_problem[0]}
              <span
                className={commonStyles.Link}
                onClick={() => setShowLoginGuideModal(true)}
              >
                {strings.login.login_problem[1]}
              </span>
            </div>
            <p className={styles.OTPExpired}>
              {strings.login.otp_expired[0]}
              <span onClick={resendOTP}>{strings.login.otp_expired[1]}</span>
            </p>
          </>
        ) : (
          <p className={styles.ExpireTimer}>
            {strings.login.remaining_time[0]}
            <span>
              {formatStrings(strings.login.remaining_time[1], {
                time: digitsToHindi(String(expireTimer)),
              })}
            </span>
            {strings.login.remaining_time[2]}
          </p>
        )}
      </div>
      {showLoginGuideModal && <HintModal onClose={closeLoginModal} />}
      <Footer className={classNames(commonStyles.wrapper, commonStyles.footer)}>
        <Button
          text={strings.common.button.enter}
          size={ButtonSize.XLarge}
          disable={Boolean(otp.error) || !otp.value}
          onClick={handleSubmit}
          loading={loading}
        />
      </Footer>
    </>
  );
};

export default OtpEntrance;
